import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const AnalyticsChart = ({ title, data, type = 'line', height = 300 }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');

  const periods = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  const colors = {
    primary: '#2D3748',
    secondary: '#4A5568',
    accent: '#E53E3E',
    success: '#38A169',
    warning: '#D69E2E'
  };

  const pieColors = ['#2D3748', '#4A5568', '#E53E3E', '#38A169', '#D69E2E'];

  const renderChart = () => {
    switch (type) {
      case 'area':
        return (
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" stroke="#718096" fontSize={12} />
            <YAxis stroke="#718096" fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#FFFFFF', 
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                fontSize: '12px'
              }} 
            />
            <Area 
              type="monotone" 
              dataKey="value" 
              stroke={colors?.primary} 
              fill={colors?.primary}
              fillOpacity={0.1}
              strokeWidth={2}
            />
          </AreaChart>
        );
      
      case 'bar':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" stroke="#718096" fontSize={12} />
            <YAxis stroke="#718096" fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#FFFFFF', 
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                fontSize: '12px'
              }} 
            />
            <Bar dataKey="value" fill={colors?.primary} radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      
      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
            >
              {data?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={pieColors?.[index % pieColors?.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#FFFFFF', 
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                fontSize: '12px'
              }} 
            />
          </PieChart>
        );
      
      default:
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" stroke="#718096" fontSize={12} />
            <YAxis stroke="#718096" fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#FFFFFF', 
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                fontSize: '12px'
              }} 
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={colors?.primary} 
              strokeWidth={2}
              dot={{ fill: colors?.primary, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: colors?.accent }}
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        
        <div className="flex items-center space-x-2">
          <select 
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e?.target?.value)}
            className="text-sm border border-border rounded-md px-3 py-1 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            {periods?.map((period) => (
              <option key={period?.value} value={period?.value}>
                {period?.label}
              </option>
            ))}
          </select>
          
          <button className="p-1 hover:bg-muted rounded transition-micro">
            <Icon name="MoreHorizontal" size={16} className="text-muted-foreground" />
          </button>
        </div>
      </div>
      <div style={{ width: '100%', height: height }}>
        <ResponsiveContainer>
          {renderChart()}
        </ResponsiveContainer>
      </div>
      {type === 'pie' && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          {data?.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: pieColors?.[index % pieColors?.length] }}
              />
              <span className="text-sm text-muted-foreground">{item?.name}</span>
              <span className="text-sm font-medium text-foreground ml-auto">{item?.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnalyticsChart;